<script setup lang="ts">
import { computed } from 'vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { STATUS_LABEL } from './issue-enums';

interface Props {
  current: number;
  disabled?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{ change: [status: number] }>();

// Valid forward transitions per IssueStatus enum:
// 0=New, 1=Acknowledged, 2=InProgress, 3=Resolved, 4=Ignored, 5=Escalated
const transitions = computed((): { status: number; label: string; variant: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' }[] => {
  const s = props.current;
  if (s === 0 /* New */) {
    return [
      { status: 1, label: 'Acknowledge', variant: 'primary' },
      { status: 2, label: 'Mark In Progress', variant: 'secondary' },
      { status: 4, label: 'Ignore', variant: 'ghost' },
    ];
  }
  if (s === 1 /* Acknowledged */) {
    return [
      { status: 2, label: 'Mark In Progress', variant: 'primary' },
      { status: 3, label: 'Mark Resolved', variant: 'success' },
      { status: 5, label: 'Escalate', variant: 'danger' },
    ];
  }
  if (s === 2 /* InProgress */) {
    return [
      { status: 3, label: 'Mark Resolved', variant: 'success' },
      { status: 5, label: 'Escalate', variant: 'danger' },
    ];
  }
  if (s === 3 /* Resolved */ || s === 4 /* Ignored */) {
    return [
      { status: 0, label: 'Re-open', variant: 'secondary' },
    ];
  }
  if (s === 5 /* Escalated */) {
    return [
      { status: 2, label: 'Back to In Progress', variant: 'secondary' },
      { status: 3, label: 'Mark Resolved', variant: 'success' },
    ];
  }
  return [];
});
</script>

<template>
  <div class="flex flex-wrap gap-2" data-testid="status-changer">
    <BaseButton
      v-for="t in transitions"
      :key="t.status"
      :variant="t.variant"
      size="sm"
      :disabled="disabled"
      @click="emit('change', t.status)"
    >
      {{ t.label }}
    </BaseButton>
    <p v-if="transitions.length === 0" class="text-caption text-muted">
      Current status: {{ STATUS_LABEL[current] ?? String(current) }}
    </p>
  </div>
</template>
